import { Table } from '@/components/ui/shared/Table';
import ProfileLink from '@/scenes/Management/Community/ProfileLink';
import { truncateContent, truncateEntry } from '@/utils/helpers/strings';
import { Link } from 'react-router-dom';
import { DiscussionComments } from './DiscussionComments';
import css from './DiscussionsPage.module.css';
import type { DiscussionEntry, FilterMode } from './types';

const LABELS = {
  colTopic: 'Тема',
  colBlog: 'Блог',
  colComments: 'Комментарии',
  colMentions: 'Упоминания',
  colDate: 'Дата',
  empty: 'Обсуждений пока нет'
};

function getUnreadCount(entry: DiscussionEntry): number {
  return (entry.comments ?? []).filter(comment => comment.state === 'new').length;
}

function TopicCell({ entry }: { entry: DiscussionEntry }) {
  const blogSlug = entry.profile?.blogSlug ?? '';
  const entryHref = `/blog/${blogSlug}/${entry.id}`;
  const entryTitle = truncateEntry({ title: entry.title, content: entry.content }, 50);
  const contentText = truncateContent(entry.content);
  const unreadCount = getUnreadCount(entry);
  const hasUnread = unreadCount > 0;

  return (
    <div className={css.topicContent}>
      <div className={css.entryTitleRow}>
        {unreadCount > 0 && (
          <span className={`${css.unreadMessagesCollapsed} ${css.unreadMessagesMark}`}>{unreadCount}</span>
        )}
        <Link to={entryHref} className={`${css.entryLink}${hasUnread ? ` ${css.entryLinkUnread}` : ''}`}>
          {entryTitle}
        </Link>
      </div>
      {contentText && <div className={css.entryContent}>{contentText}</div>}
    </div>
  );
}

interface Props {
  discussions: DiscussionEntry[];
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  showPagination: boolean;
  filterMode: FilterMode;
}

export function DiscussionsList({
  discussions,
  totalRecords,
  currentPage,
  pageSize,
  showPagination,
  filterMode
}: Props) {
  const showBlogColumn = filterMode === 'otherBlogs' || filterMode === 'mentions';
  const commentsLabel = filterMode === 'mentions' ? LABELS.colMentions : LABELS.colComments;

  if (discussions.length === 0) {
    return <div className={css.empty}>{LABELS.empty}</div>;
  }

  return (
    <Table
      expandable
      pagination={showPagination ? { totalRecords, currentPage, pageSize, backgroundColor: null } : undefined}
    >
      <Table.Head>
        <Table.HeaderCell className={css.colTopic}>{LABELS.colTopic}</Table.HeaderCell>
        {showBlogColumn && <Table.HeaderCell className={css.colBlog}>{LABELS.colBlog}</Table.HeaderCell>}
        <Table.HeaderCell width="110px" align="center" hideBelow="sm">
          {commentsLabel}
        </Table.HeaderCell>
        <Table.HeaderCell width="90px" align="center" hideBelow="md">
          {LABELS.colDate}
        </Table.HeaderCell>
      </Table.Head>
      <Table.Body>
        {discussions.map(entry => {
          const blogSlug = entry.profile?.blogSlug ?? '';

          return (
            <Table.Row
              key={entry.id}
              rowId={entry.id}
              expanded={
                <DiscussionComments comments={entry.comments ?? []} blogSlug={blogSlug} entryId={entry.id} />
              }
            >
              <Table.Cell className={css.colTopic}>
                <TopicCell entry={entry} />
              </Table.Cell>
              {showBlogColumn && (
                <Table.Cell className={css.colBlog}>
                  {entry.profile ? (
                    <ProfileLink profile={entry.profile} link={`/profile/${entry.profile.id}`}>
                      <span className={css.blogTitle}>{entry.profile.blogTitle}</span>
                    </ProfileLink>
                  ) : null}
                </Table.Cell>
              )}
              <Table.CountCell
                width="110px"
                hideBelow="sm"
                total={entry.meta.comments}
                unread={getUnreadCount(entry)}
              />
              <Table.DateCell width="90px" hideBelow="md" date={entry.publishedAt} />
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}
